import type { ShopifyResultQuestionSelection } from '@testfeed/shopify-contracts'
import { requestShopifyNavigation } from './shopifyNavigation'
import type { useAppBridge } from '@shopify/app-bridge-react'
/**
 * What: Encodes Shopify embedded-app navigation as path routes with query state only for selected records.
 * Transport / data-flow: Browser URL helpers used by React components and Shopify Navigation API-compatible links.
 *
 * Design:
 * Complies with the Shopify App Bridge v4 Navigation API specifications. Top-level flat routes
 * are prefix-matched on `<ui-nav-menu>` components (Home, Studies, Settings) to keep
 * native navigation tabs highlighted. Custom query parameter preservation prevents iframe session
 * breakage during local client routing transitions.
 *
 */
import { isShopifyForkChangeReason, type ShopifyForkChangeReason } from '../src/shared/fork-lineage'
export type ShopifyAppViewState =
  | { view: 'home' }
  | {
      view: 'study-builder'
      /** Old authoring URL markers are retained only to render the retirement notice. */
      retired?: true
      studyId?: string
      setupId?: string
      draftId?: string
      step?: ShopifyStudyBuilderStep
      section?: string
      // Fork lineage (issue #1143, Phase 1). Both present together mark a "New version" fork so the
      // builder pre-fills from the parent and stamps the lineage edge on submit.
      forkParentPacketId?: string
      forkChangeReason?: ShopifyForkChangeReason
      /** `new` marks a draft ID minted for a brand-new test. A missing draft without it is an error, never a blank test. */
      entry?: 'new'
    }
  | { view: 'studies' }
  | { view: 'study-run'; setupId: string; packetId?: string; studyId?: string }
  | { view: 'micro-surveys' }
  | { view: 'pixel-events' }
  | { view: 'insights' }
  | { view: 'settings' }
  | { view: 'settings-integrations' }
  | { view: 'support'; setupId?: string; packetId?: string; runId?: string; latestStepKey?: string }
  | {
      view: 'direct-study-results'
      studyId: string
      runId: string | null
      questionSelection?: ShopifyResultQuestionSelection
    }
  | { view: 'results'; packetId: string; runId: string | null }

export const SHOPIFY_STUDY_BUILDER_STEPS = ['survey', 'audience', 'brief', 'preflight'] as const

export type ShopifyStudyBuilderStep = (typeof SHOPIFY_STUDY_BUILDER_STEPS)[number]

// Preserve embedded context and noncredential cleanup selectors through navigation/reload. Active
// readiness clears recovery; selectors never authorize a read or mutation. `testfeed_connected`
// remains one-shot so the dismissible connected banner does not recur on navigation.
const EMBEDDED_CONTEXT_QUERY_KEYS = [
  'shop',
  'host',
  'embedded',
  'appLoadId',
  'cleanupConnectionId',
  'disconnectActivatedAt',
]

function buildContextSearchParams() {
  const search = typeof window !== 'undefined' ? window.location.search : ''
  const currentParams = new URLSearchParams(search)
  const contextParams = new URLSearchParams()
  for (const key of EMBEDDED_CONTEXT_QUERY_KEYS) {
    const value = currentParams.get(key)
    if (value) contextParams.set(key, value)
  }
  return contextParams
}

function assertCompleteForkLineage(options: {
  forkParentPacketId?: string
  forkChangeReason?: ShopifyForkChangeReason
}) {
  const hasForkParentPacketId = Boolean(options.forkParentPacketId?.trim())
  const hasForkChangeReason = Boolean(options.forkChangeReason)

  if (hasForkParentPacketId === hasForkChangeReason) {
    return
  }

  if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
    const warningReason = hasForkParentPacketId
      ? 'forkParentPacketId provided without forkChangeReason'
      : 'forkChangeReason provided without forkParentPacketId'

    console.error(`[shopify-results-navigation] Incomplete fork lineage fields: ${warningReason}.`, options)
  }
}

function buildAppPathUrl(pathname: string) {
  const params = buildContextSearchParams()
  const queryString = params.toString()
  if (!queryString) return pathname
  return `${pathname}?${queryString}`
}

/** Adds the current embedded transport context to a server-selected relative app route; chooses no destination. */
export function withShopifyAppContext(href: string): string {
  if (!href.startsWith('/') || href.startsWith('//')) throw new Error('Expected a relative Shopify app route.')
  const url = new URL(href, 'https://shopify-app.invalid')
  for (const [key, value] of buildContextSearchParams()) {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value)
  }
  return `${url.pathname}${url.search}${url.hash}`
}

/** Builds a tests-scoped results URL so App Bridge keeps the Tests navigation context active. */
export function buildResultsViewUrl(packetId: string, runId: string | null = null) {
  const params = buildContextSearchParams()
  params.set('packetId', packetId)
  if (runId) {
    params.set('runId', runId)
  } else {
    params.delete('runId')
  }
  return `/tests/results?${params.toString()}`
}

/** Direct studies use their own identity in results URLs; no packet is fabricated. */
export function buildDirectStudyResultsViewUrl(studyId: string, runId: string | null = null) {
  const params = buildContextSearchParams()
  params.set('studyId', studyId)
  if (runId) params.set('runId', runId)
  return `/tests/study-results?${params.toString()}`
}

function parseBuilderStep(value: string | null): ShopifyStudyBuilderStep {
  return SHOPIFY_STUDY_BUILDER_STEPS.includes(value as ShopifyStudyBuilderStep)
    ? (value as ShopifyStudyBuilderStep)
    : 'survey'
}

/** Builds the study builder route used for creating, editing, or forking a setup into a new version. */
export function buildStudyBuilderViewUrl(
  options: {
    studyId?: string
    setupId?: string
    draftId?: string
    step?: ShopifyStudyBuilderStep
    section?: string
    forkParentPacketId?: string
    forkChangeReason?: ShopifyForkChangeReason
    entry?: 'new'
  } = {},
) {
  assertCompleteForkLineage(options)
  const params = buildContextSearchParams()
  if (options.studyId) params.set('studyId', options.studyId)
  if (options.section) params.set('section', options.section)
  if (options.setupId) params.set('setupId', options.setupId)
  if (options.draftId) params.set('draftId', options.draftId)
  if (options.entry === 'new') params.set('new', '1')
  if (options.step && options.step !== 'survey') params.set('step', options.step)
  if (options.forkParentPacketId && options.forkChangeReason) {
    params.set('forkParentPacketId', options.forkParentPacketId)
    params.set('forkChangeReason', options.forkChangeReason)
  }
  const queryString = params.toString()
  if (!queryString) return '/study-builder'
  return `/study-builder?${queryString}`
}

type StudyBuilderView = Extract<ShopifyAppViewState, { view: 'study-builder' }>

/** Rewrites the address bar for the open builder without navigating; `entry: 'new'` adds or (omitted) drops the marker. */
export function replaceStudyBuilderUrl(view: StudyBuilderView, entry?: 'new') {
  globalThis.window?.history.replaceState(
    null,
    '',
    buildStudyBuilderViewUrl({
      studyId: view.studyId,
      setupId: view.setupId,
      draftId: view.draftId,
      step: view.step,
      section: view.section,
      forkParentPacketId: view.forkParentPacketId,
      forkChangeReason: view.forkChangeReason,
      ...(entry ? { entry } : {}),
    }),
  )
}

/** A builder opened without a draft ID is a brand-new test: mint the ID and mark it `new` so a missing draft is expected. */
export function ensureStudyBuilderDraftIdentity(view: ShopifyAppViewState): ShopifyAppViewState {
  if (
    view.view !== 'study-builder' ||
    view.retired ||
    view.setupId ||
    view.forkParentPacketId ||
    view.forkChangeReason ||
    view.draftId ||
    view.studyId
  )
    return view
  const next: StudyBuilderView = { ...view, draftId: crypto.randomUUID(), entry: 'new' }
  replaceStudyBuilderUrl(next, 'new')
  return next
}

/** Builds the home route with the shared embedded Shopify context. */
export function buildHomeViewUrl() {
  return buildAppPathUrl('/')
}

/** Builds the Tests path route used by Shopify App Bridge to determine active navigation state. */
export function buildTestsViewUrl() {
  return buildAppPathUrl('/tests')
}

/** Builds the saved-setup review route that leads directly to a paid StudyRun confirmation. */
export function buildStudyRunViewUrl(options: { setupId: string; packetId?: string; studyId?: string }) {
  const params = buildContextSearchParams()
  if (options.studyId) params.set('studyId', options.studyId)
  else params.set('setupId', options.setupId)
  if (options.packetId) params.set('packetId', options.packetId)
  const queryString = params.toString()
  return `/tests/run?${queryString}`
}

/** Builds the Micro-surveys path route used by Shopify App Bridge to determine active navigation state. */
export function buildMicroSurveysViewUrl() {
  return buildAppPathUrl('/micro-surveys')
}

/** Builds the Pixel events route used for storefront Web Pixel operational diagnostics. */
export function buildPixelEventsViewUrl() {
  return buildAppPathUrl('/pixel-events')
}

/** Builds the Insights path route used by Shopify App Bridge to determine active navigation state. */
export function buildInsightsViewUrl() {
  return buildAppPathUrl('/insights')
}

/** Builds the Settings path route where TestFeed account connection controls live. */
export function buildSettingsViewUrl() {
  return buildAppPathUrl('/settings')
}
export function buildSettingsIntegrationsViewUrl() {
  return buildAppPathUrl('/settings/integrations')
}

/** Builds the support route used by Shopify's Get support action and contextual failed-run links. */
export function buildSupportViewUrl(
  options: { setupId?: string; packetId?: string; runId?: string; latestStepKey?: string } = {},
) {
  const params = buildContextSearchParams()
  if (options.setupId) params.set('setupId', options.setupId)
  if (options.packetId) params.set('packetId', options.packetId)
  if (options.runId) params.set('runId', options.runId)
  if (options.latestStepKey) params.set('latestStepKey', options.latestStepKey)
  const queryString = params.toString()
  if (!queryString) return '/support'
  return `/support?${queryString}`
}

export const CALENDLY_BASE_URL = 'https://calendly.com/testfeed/testfeed'

const CALENDLY_SOURCE_LABELS = {
  settings: 'Settings',
  study_builder: 'Study Builder',
  results_debrief: 'Results debrief',
} as const

export type CalendlyBookingSource = keyof typeof CALENDLY_SOURCE_LABELS | 'general' | (string & {})

export type CalendlyBookingOptions = {
  baseUrl?: string
  source?: CalendlyBookingSource
  shopDomain?: string
  setupId?: string
  packetId?: string
  runId?: string
}

/** Builds a single human-readable context line for Calendly's first custom question, since the account only has one shared event type. */
function buildCalendlyContextLine(options: CalendlyBookingOptions): string {
  const parts = [
    options.shopDomain && `Shop: ${options.shopDomain}`,
    options.setupId && `Setup: ${options.setupId}`,
    options.packetId && `Result: ${options.packetId}`,
    options.runId && `Run: ${options.runId}`,
  ].filter(Boolean)
  const label =
    options.source && options.source in CALENDLY_SOURCE_LABELS
      ? CALENDLY_SOURCE_LABELS[options.source as keyof typeof CALENDLY_SOURCE_LABELS]
      : undefined
  if (label && parts.length) return `${label} — ${parts.join(', ')}`
  return label ?? parts.join(', ')
}

/** Builds an external Calendly booking URL with UTM attribution, a pre-filled context line, and Polaris branding. */
export function buildCalendlyBookingUrl(options: CalendlyBookingOptions = {}): string {
  const targetBase = options.baseUrl ?? CALENDLY_BASE_URL

  try {
    const url = new URL(targetBase)
    if (options.source) {
      url.searchParams.set('utm_source', 'shopify_app')
      url.searchParams.set('utm_medium', 'embedded_app')
      url.searchParams.set('utm_campaign', options.source)
    }
    const contextLine = buildCalendlyContextLine(options)
    if (contextLine) url.searchParams.set('a1', contextLine)
    url.searchParams.set('hide_gdpr_banner', '1')
    url.searchParams.set('primary_color', '008060')
    return url.toString()
  } catch (error) {
    console.error('Failed to construct Calendly booking URL', error)
    return targetBase
  }
}

export const DEFAULT_CALENDLY_URL = CALENDLY_BASE_URL

/** Parses the app view from path or query state while treating incomplete results state as home. */
export function parseShopifyAppView(pathOrSearch: string, search = ''): ShopifyAppViewState {
  const [rawPath = '/', inlineSearch = ''] = pathOrSearch.split('?')
  const pathname = pathOrSearch.startsWith('?') ? '/' : rawPath.replace(/\/+$/, '') || '/'
  const searchSource = pathOrSearch.startsWith('?') ? pathOrSearch : search || inlineSearch
  const params = new URLSearchParams(searchSource)
  const view = params.get('view')
  const packetId = params.get('packetId')
  const studyId = params.get('studyId')?.trim() || undefined
  const setupId = params.get('setupId')?.trim() || undefined
  const draftId = params.get('draftId')?.trim() || undefined
  const supportPacketId = params.get('packetId')?.trim() || undefined
  const supportRunId = params.get('runId')?.trim() || undefined
  const latestStepKey = params.get('latestStepKey')?.trim() || undefined
  const retiredBuilder =
    params.has('setupId') || params.has('forkParentPacketId') || params.has('forkChangeReason')
      ? ({ retired: true } as const)
      : {}
  const step = parseBuilderStep(params.get('step'))
  const section = params.get('section')?.trim() || undefined
  const entry = params.get('new') === '1' ? ({ entry: 'new' } as const) : {}
  const forkParentPacketId = params.get('forkParentPacketId')?.trim() || undefined
  const rawForkChangeReason = params.get('forkChangeReason')?.trim()
  // A fork edge is only honored when both halves are present and the reason is valid — otherwise the
  // builder falls back to a plain edit rather than assuming a silent default.
  const forkFields =
    forkParentPacketId && isShopifyForkChangeReason(rawForkChangeReason)
      ? { forkParentPacketId, forkChangeReason: rawForkChangeReason }
      : {}
  if (pathname === '/tests/study-results' && studyId)
    return {
      view: 'direct-study-results',
      studyId,
      runId: params.get('runId'),
      questionSelection: {
        phrasesPage: params.get('phrasesPage') ?? undefined,
        nextTestsPage: params.get('nextTestsPage') ?? undefined,
        questionId: params.get('questionId') ?? undefined,
        answerArmId: params.get('answerArmId') ?? undefined,
        answerPage: params.get('answerPage') ?? undefined,
      },
    }
  if (pathname === '/tests') return { view: 'studies' }
  if (pathname === '/tests/run' && studyId) return { view: 'study-run', setupId: studyId, studyId }
  if (pathname === '/tests/run') return { view: 'study-run', setupId: setupId ?? '', ...(packetId ? { packetId } : {}) }
  if (pathname === '/micro-surveys') return { view: 'micro-surveys' }
  if (pathname === '/pixel-events') return { view: 'pixel-events' }
  if (pathname === '/insights') return { view: 'insights' }
  if (pathname === '/settings/integrations') return { view: 'settings-integrations' }
  if (pathname === '/settings') return { view: 'settings' }
  if (pathname === '/support')
    return {
      view: 'support',
      ...(setupId ? { setupId } : {}),
      ...(supportPacketId ? { packetId: supportPacketId } : {}),
      ...(supportRunId ? { runId: supportRunId } : {}),
      ...(latestStepKey ? { latestStepKey } : {}),
    }
  if (view === 'studies') return { view: 'studies' }
  if (view === 'micro-surveys') return { view: 'micro-surveys' }
  if (view === 'pixel-events') return { view: 'pixel-events' }
  if (view === 'insights') return { view: 'insights' }
  if (view === 'settings') return { view: 'settings' }
  if (view === 'support')
    return {
      view: 'support',
      ...(setupId ? { setupId } : {}),
      ...(supportPacketId ? { packetId: supportPacketId } : {}),
      ...(supportRunId ? { runId: supportRunId } : {}),
      ...(latestStepKey ? { latestStepKey } : {}),
    }
  if (view === 'study-builder')
    return {
      view: 'study-builder',
      ...retiredBuilder,
      ...(studyId ? { studyId } : {}),
      ...(setupId ? { setupId } : {}),
      ...(draftId ? { draftId } : {}),
      step,
      ...(section ? { section } : {}),
      ...forkFields,
      ...entry,
    }
  if (pathname === '/study-builder')
    return {
      view: 'study-builder',
      ...retiredBuilder,
      ...(studyId ? { studyId } : {}),
      ...(setupId ? { setupId } : {}),
      ...(draftId ? { draftId } : {}),
      step,
      ...(section ? { section } : {}),
      ...forkFields,
      ...entry,
    }
  if (pathname !== '/tests/results' && view !== 'results') {
    return { view: 'home' }
  }
  if (!packetId) return { view: 'home' }
  return {
    view: 'results',
    packetId,
    runId: params.get('runId'),
  }
}

/** The native Save Bar owns confirmation; callers navigate only after it resolves. No separate dirty-state registry. */
export async function leaveShopifyBuilder(shopify: ReturnType<typeof useAppBridge>, navigate: () => void) {
  return requestShopifyNavigation(shopify, navigate)
}
